import "../styles/info-account-renewal.css";
import { Link } from "react-router-dom";
import { usePageSEO } from "../lib/usePageSEO";

const EFFECTIVE_DATE = "2026년 10월 2일";

export function PrivacyPolicy() {
  usePageSEO({
    title: "개인정보처리방침 | Foreducator",
    description:
      "Foreducator(포에듀케이터)의 개인정보 수집·이용, 보유기간, 위탁, 이용자 권리 및 만 14세 미만 처리에 관한 방침입니다.",
    path: "/privacy",
  });

  return (
    <div className="fe-page-canvas min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <article className="fe-surface max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-10 text-gray-800">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">개인정보처리방침</h1>
        <p className="text-sm text-gray-500 mb-8">
          Foreducator(이하 &quot;서비스&quot;)는 개인정보보호법 등 관련 법령에 따라 이용자의 개인정보를 보호하고, 이와 관련한
          고충을 신속히 처리하기 위하여 다음과 같이 개인정보처리방침을 둡니다. 시행일: {EFFECTIVE_DATE}
        </p>

        <nav className="text-sm text-gray-600 mb-10 p-4 bg-gray-50 rounded-xl border border-gray-100">
          <p className="font-semibold text-gray-800 mb-2">목차</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>개인정보의 처리 목적 및 수집·이용 항목, 보유기간</li>
            <li>개인정보의 안전성 확보 조치</li>
            <li>개인정보 열람·정정·삭제·처리정지 요구</li>
            <li>만 14세 미만 아동의 개인정보</li>
            <li>개인정보 보호책임자, 제3자 제공, 처리 위탁</li>
            <li>쿠키·로컬 저장소, 국외 이전, 정책 변경</li>
          </ol>
        </nav>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">1. 개인정보의 처리 목적 및 수집·이용 항목, 보유기간</h2>
          <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">1-1. 최소 수집 원칙</h3>
          <p className="text-sm leading-relaxed mb-4">
            서비스는 개인정보처리 목적에 필요한 최소한의 개인정보만을 수집하며, 수집 시점에 이용 목적을 명확히 안내하고 동의를
            받는 범위에서 처리합니다.
          </p>
          <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">1-2. 처리 목적</h3>
          <ul className="list-disc list-inside text-sm leading-relaxed space-y-1 mb-4">
            <li>회원 가입·로그인·본인 확인 및 계정 관리</li>
            <li>교사용 워크스페이스 기능 제공(학급·업무 도구, 커뮤니티, 메시지, 시간표 등 서비스 화면에 명시된 기능)</li>
            <li>부정 이용 방지, 분쟁 조정, 법령상 의무 이행</li>
            <li>문의·민원 처리 및 고지·통지</li>
            <li>일부 기능에서 입력하신 내용을 활용한 AI 기반 생성·분석(해당 기능 이용 시 서버로 전송되는 범위 내)</li>
            <li>서비스 품질·보안·성능 개선을 위한 통계(가명·익명 처리 가능한 경우 포함)</li>
            <li>Google Analytics를 통한 이용 분석, Google AdSense 광고 제공 및 이메일 수신 설정에 따른 서비스 소식 안내</li>
          </ul>
          <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">1-3. 수집 항목, 보유·이용 기간</h3>
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full text-sm border border-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-200 px-3 py-2 text-left font-semibold">구분</th>
                  <th className="border border-gray-200 px-3 py-2 text-left font-semibold">항목(예시)</th>
                  <th className="border border-gray-200 px-3 py-2 text-left font-semibold">보유·이용 기간</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-200 px-3 py-2 align-top">회원(인증)</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">
                    이메일 주소, 비밀번호(암호화·해시 등 안전한 방식으로 저장), 소셜 로그인 시 해당 제공사가 전달하는 식별자
                    (Google, 카카오 등)
                  </td>
                  <td className="border border-gray-200 px-3 py-2 align-top">
                    회원 탈퇴 또는 이용계약 종료 시 지체 없이 파기합니다. 다만 관계 법령에 따라 보관이 필요한 경우 해당 기간
                    동안 별도 보관할 수 있습니다.
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-3 py-2 align-top">회원(프로필)</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">
                    닉네임·이름 등 계정 설정 화면에서 입력·연동되는 정보
                  </td>
                  <td className="border border-gray-200 px-3 py-2 align-top">위와 동일</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-3 py-2 align-top">서비스 이용 과정</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">
                    각 기능 이용 시 이용자가 입력·업로드·게시한 정보(게시글, 쪽지, 파일, 설문·동의서·서명·학급 활동 등 해당
                    기능에 따라 상이)
                  </td>
                  <td className="border border-gray-200 px-3 py-2 align-top">
                    원칙적으로 회원 탈퇴 시 삭제·파기를 진행합니다. 다만 다른 이용자에게 공개된 게시물·통계성 데이터, 법령상
                    보존 대상, 백업·로그 등은 일정 기간 보관되거나 익명화 후 보관될 수 있습니다.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            이용자가 서비스 내에 입력하는 학생 등 제3자에 관한 정보는 해당 교육·업무 목적에 따라 이용자가 관리·동의 책임을
            부담하는 경우가 있으며, 서비스는 제공된 기능 범위에서만 처리합니다.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">2. 개인정보의 안전성 확보 조치</h2>
          <p className="text-sm leading-relaxed mb-3">
            서비스는 개인정보의 분실·도난·유출·변조·훼손을 방지하기 위하여 다음과 같은 조치를 취합니다.
          </p>
          <ul className="list-disc list-inside text-sm leading-relaxed space-y-2">
            <li>
              <strong className="text-gray-900">기술적 조치:</strong> 전송 구간 암호화(HTTPS 등), 접근 권한 관리, 비밀번호 등
              중요 정보의 안전한 저장, 보안 취약점에 대한 조치, 침해 사고 대응
            </li>
            <li>
              <strong className="text-gray-900">관리적 조치:</strong> 개인정보 접근 권한 최소화, 위탁사 관리, 내부 관리계획에
              따른 점검
            </li>
            <li>
              <strong className="text-gray-900">물리적 조치:</strong> 개인정보를 보관하는 전산 시설 및 클라우드 인프라에 대한
              출입·접근 통제(위탁사 정책 포함)
            </li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">3. 개인정보 열람·정정·삭제·처리정지 요구</h2>
          <p className="text-sm leading-relaxed mb-3">
            이용자는 언제든지 개인정보 열람·정정·삭제·처리정지를 요구할 수 있습니다. 서비스 내에서 직접 할 수 있는 방법과
            운영자에게 요청하는 방법을 안내합니다.
          </p>
          <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">3-1. 서비스 내 조치</h3>
          <ul className="list-disc list-inside text-sm leading-relaxed space-y-2 mb-4">
            <li>
              <strong className="text-gray-900">정정·일부 열람:</strong> 로그인 후{" "}
              <Link to="/account/profile" className="text-blue-600 hover:underline">
                계정·프로필 설정(/account/profile)
              </Link>
              에서 수정할 수 있는 항목을 변경할 수 있습니다.
            </li>
            <li>
              <strong className="text-gray-900">비밀번호 변경:</strong>{" "}
              <Link to="/update-password" className="text-blue-600 hover:underline">
                비밀번호 재설정·변경(/update-password)
              </Link>
              절차를 이용할 수 있습니다(이메일 인증 등 설정에 따름).
            </li>
            <li>
              <strong className="text-gray-900">삭제(회원 탈퇴):</strong>{" "}
              <Link to="/account/profile" className="text-blue-600 hover:underline">
                계정 설정
              </Link>
              에서 「계정」 → 「회원 탈퇴」 → 「다음」을 누르고, 최종 확인 화면에 「탈퇴」를 입력하여 계정 삭제를 요청할 수 있습니다.
              로그인 계정, 프로필 및 계정에 연결된 서비스 데이터와 알림 구독을 삭제합니다. 탈퇴 후 일부 데이터는 법령·기술적 사유로 일정 기간
              보관되거나 익명화될 수 있으며, 그 범위는 위 보유기간 기준을 따릅니다.
            </li>
          </ul>
          <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">3-2. 운영자에게 요청</h3>
          <p className="text-sm leading-relaxed mb-2">
            앱을 설치하지 않아도 hwanys2@naver.com으로 제목 「Foreducator 계정 삭제 요청」과 가입 이메일을 보내 계정 및
            관련 데이터 삭제를 요청할 수 있습니다. 계정을 유지하면서 일부 데이터만 삭제하려면 제목 「Foreducator 데이터 삭제 요청」과
            삭제할 데이터·기능을 적어 주세요. 비밀번호는 보내지 마세요. 본인 확인을 위해 추가 정보를 요청할 수 있으며,
            관련 법령이 정한 기한 내에 조치·회신 드리겠습니다.
          </p>
          <p className="text-sm text-gray-600">
            <strong className="text-gray-800">처리정지:</strong> 정보주체의 권리에 따른 처리정지 요구가 법령에 특별한 규정이
            있는 경우 등에는 제한될 수 있습니다. 서비스 소식 이메일은 계정 설정의 이메일 수신 설정이나 이메일 하단의
            「이메일 구독 취소하기」에서 수신을 거부할 수 있습니다.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">4. 만 14세 미만 아동의 개인정보</h2>
          <p className="text-sm leading-relaxed mb-3">
            서비스는 주로 성인 교사 등 교육 현장의 이용자를 대상으로 합니다. <strong className="text-gray-900">만 14세 미만</strong>
            인 정보주체에 대해서는 원칙적으로 회원가입을 받지 않으며, 이메일 가입 시 만 14세 이상임을 확인합니다.
          </p>
          <p className="text-sm leading-relaxed">
            법정대리인의 동의가 필요한 개인정보 처리가 불가피한 경우, 법정대리인의 연락처·동의 확인 등 합리적인 절차를 거친
            후에만 처리하며, 법정대리인은 아동의 개인정보에 대한 열람·정정·삭제·처리정지를 요청할 수 있습니다. 관련 문의는
            개인정보 보호책임자 이메일로 연락해 주시기 바랍니다.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">5. 개인정보 보호책임자, 제3자 제공, 처리 위탁</h2>
          <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">5-1. 개인정보 보호책임자</h3>
          <ul className="text-sm leading-relaxed space-y-1 mb-6 list-none pl-0">
            <li>책임자: 박진환</li>
            <li>
              이메일:{" "}
              <a href="mailto:hwanys2@naver.com" className="text-blue-600 hover:underline">
                hwanys2@naver.com
              </a>
            </li>
            <li>문의 시 성함·연락 가능한 이메일·요청 사항을 적어 보내 주시면 빠르게 처리하겠습니다.</li>
          </ul>
          <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">5-2. 개인정보의 제3자 제공</h3>
          <p className="text-sm leading-relaxed mb-6">
            이용자가 공유·게시하는 콘텐츠는 선택한 공개 범위에 따라 다른 이용자에게 제공됩니다. Google Analytics와
            Google AdSense는 이용 분석·광고 제공을 위해 기기·브라우저 정보, 쿠키 식별자, IP 주소에서 추정되는 대략적인 위치,
            페이지 이용 및 상호작용 정보를 처리할 수 있습니다. 개인정보를 별도로 판매·임대하지 않습니다.
          </p>
          <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">5-3. 개인정보 처리 위탁</h3>
          <p className="text-sm leading-relaxed mb-3">
            서비스 운영을 위해 다음과 같이 개인정보 처리 업무를 위탁할 수 있습니다. 위탁 계약 시 관련 법령에 따라 위탁 업무
            수행 목적 외 처리 금지, 기술적·관리적 보호조치, 재위탁 제한, 안전성 확보 조치 등을 문서로 명시합니다.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-200 px-3 py-2 text-left font-semibold">수탁자</th>
                  <th className="border border-gray-200 px-3 py-2 text-left font-semibold">위탁 목적</th>
                  <th className="border border-gray-200 px-3 py-2 text-left font-semibold">위탁하는 항목(유형)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-200 px-3 py-2 align-top">Supabase Inc.</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">인증·데이터베이스·관련 인프라 운영</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">계정·인증 정보, 서비스 저장 데이터</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-3 py-2 align-top">Vercel Inc.</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">웹 애플리케이션·서버리스 함수 호스팅</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">이용 과정에서 처리되는 정보(전송·일시 저장 등)</td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-3 py-2 align-top">OpenAI 등 AI 추론 제공사</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">AI 기반 생성·분석 기능 제공</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">
                    해당 기능 이용 시 전송되는 입력 텍스트·파일 등(기능별 상이)
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-200 px-3 py-2 align-top">Google LLC, 카카오 등</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">소셜 계정 로그인 연동</td>
                  <td className="border border-gray-200 px-3 py-2 align-top">해당 사업자가 제공하는 식별자·프로필(설정에 따름)</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-600 mt-3 leading-relaxed">
            소셜 로그인 시 각 사업자의 개인정보처리방침이 추가로 적용됩니다. 수탁자·위탁 업무 내용은 서비스 변경 시 본 방침을
            통해 갱신될 수 있습니다.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">6. 쿠키·로컬 저장소, 국외 이전, 정책 변경</h2>
          <h3 className="text-lg font-semibold text-gray-900 mt-2 mb-2">6-1. 쿠키·로컬 저장소</h3>
          <p className="text-sm leading-relaxed mb-4">
            로그인 유지·보안을 위해 브라우저 로컬 저장소 등에 인증 토큰 등이 저장될 수 있습니다. 브라우저 설정에서 삭제할 수
            있으며, 삭제 시 재로그인이 필요할 수 있습니다.
          </p>
          <h3 className="text-lg font-semibold text-gray-900 mt-2 mb-2">앱·브라우저 알림</h3>
          <p className="text-sm leading-relaxed mb-4">
            이용자가 기기 알림을 허용하면 댓글·쪽지·공유 등의 소식을 전달하기 위해 계정 식별자, 푸시 구독 주소,
            구독 암호화 키, 브라우저 정보 및 구독 시각을 처리합니다. 알림은 브라우저·운영체제의 푸시 서비스를
            통해 전달되며 알림 내용이 잠금 화면에 표시될 수 있습니다. 알림 화면에서 이 기기의 수신을 끄거나
            휴대폰 설정에서 권한을 철회할 수 있습니다. 로그아웃 시 현재 기기의 구독을 해제하며, 계정 삭제 시
            연결된 구독 정보를 삭제합니다. 수신 해제 또는 만료된 구독은 발송 대상에서 제외됩니다.
          </p>
          <h3 className="text-lg font-semibold text-gray-900 mt-2 mb-2">6-2. 국외 이전</h3>
          <p className="text-sm leading-relaxed mb-4">
            위탁사·기술사의 서버가 해외에 위치할 수 있어, 개인정보가 국외로 이전되어 처리될 수 있습니다. 이전 국가·시기·항목
            등은 이용 중인 서비스·설정에 따라 달라지며, 필요 시 보호책임자에게 문의하시면 관련 안내를 드리겠습니다.
          </p>
          <h3 className="text-lg font-semibold text-gray-900 mt-2 mb-2">6-3. 방침 변경</h3>
          <p className="text-sm leading-relaxed">
            법령·서비스 변경에 따라 본 방침을 개정할 수 있으며, 변경 시 시행일과 함께 서비스 내 게시 등 합리적인 방법으로
            고지합니다.
          </p>
        </section>

        <p className="text-sm text-gray-500 border-t border-gray-200 pt-6">
          기타 개인정보 침해에 대한 신고·상담은 개인정보침해신고센터(privacy.kisa.or.kr), 경찰청 사이버수사국 등에 문의하실 수
          있습니다.
        </p>
      </article>
    </div>
  );
}
